import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import { describeProposalOperation } from "work/proposals/negotiation/proposal-change-descriptors";
import { proposalFieldForTimelinePath } from "work/proposals/negotiation/proposal-field-timeline-path";
import type { ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

/**
 * How many queued-but-unsent changes each tab is holding — the "N pending changes not yet shared" chip.
 *
 * Counted through `describeProposalOperation`, not off the raw queue, so this number is by construction
 * the number of rows the Save confirmation is about to list: an operation that dialog declines to
 * describe (projection metadata, a resolution decision, a no-op field set) is not a change a user can
 * be shown, and advertising it on a tab they then find nothing on is worse than showing nothing.
 *
 * Operations, not fields: the chip says "changes", and the Save dialog itemises one row per operation,
 * so a user can check the badge against the list. (The unviewed-change badge beside it counts FIELDS —
 * a different question, deliberately, and the two are asserted together in `badges-and-history.spec.ts`.)
 */
export function proposalPendingChangeCountsByCategory(
  operations: readonly ProposalContentOperation[] | undefined,
): Partial<Record<ProposalFieldCategoryType, number>> {
  const counts: Partial<Record<ProposalFieldCategoryType, number>> = {};
  if (!operations?.length) return counts;

  for (const operation of operations) {
    if (!describeProposalOperation(operation)) continue;
    // Resolved by field, not by string-matching the path: a fee-category edit lands on
    // "/feeSchedule/categories" while Fee Schedule's own path is "/feeSchedule". Matching on the path
    // is what once left every collection-rooted tab stuck at zero (see `proposal-field-unread.ts`).
    const category = proposalFieldForTimelinePath(operation.path)?.category;
    if (!category) continue;
    counts[category] = (counts[category] ?? 0) + 1;
  }

  return counts;
}
